import React, { useEffect, useState } from 'react';
import {
  Container, Typography, List, ListItem, ListItemText,
  IconButton, TextField, Button, Stack, CircularProgress,
  Alert, Divider, Paper, Avatar
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@mui/material';
import { getCurrentUser } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import {
  validateName, validatePhone, validateEmail,
  validateServiceName, validatePrice, validateDuration, runValidators, PHONE_RULE_KEY,
} from '../../utils/validation';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { mediaUrl } from '../../utils/media';

const initialsOf = (name) => (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

const NAME_LANGS = ['pl', 'uk', 'en'];

export default function ServiceAndProfileEditor() {
  const { t, i18n } = useTranslation();
  const { workspaceRole, activeWorkspaceId } = useAuth();
  const isManager = workspaceRole === 'owner' || workspaceRole === 'admin';
  // Керування послугами: свій майстер або (для owner/admin) будь-який працівник
  const [myId, setMyId] = useState(null);
  const [targetMasterId, setTargetMasterId] = useState('');
  const [members, setMembers] = useState([]);
  const effectiveMasterId = targetMasterId || myId;
  // ---- PROFILE ----
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', email: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileSaveLoading, setProfileSaveLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  // ---- SERVICES ----
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({ names: { pl: '', uk: '', en: '' }, price: '', duration: '', blockDuration: '' });
  const [serviceErrors, setServiceErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Редагування назв наявної послуги (трьома мовами)
  const [editingId, setEditingId] = useState(null);
  const [editNames, setEditNames] = useState({ pl: '', uk: '', en: '' });
  const [editTimes, setEditTimes] = useState({ price: '', duration: '', blockDuration: '' });
  const [editSaving, setEditSaving] = useState(false);

  // ==== LOAD PROFILE ====
  useEffect(() => {
    async function fetchProfile() {
      setProfileLoading(true);
      setProfileError('');
      try {
        const user = await getCurrentUser();
        if (!user?.id) throw new Error('User not found');
        setMyId(user.id);
        const users = await api.getUsers();
        const me = users.find(u => u.id === user.id);
        if (!me) throw new Error('User not found');
        setProfile(me);
        setProfileForm({ name: me.name || '', phone: me.phone || '', email: me.email || '' });
      } catch (e) {
        setProfileError(e.message || t('service.loadProfileError'));
      }
      setProfileLoading(false);
    }
    fetchProfile();
  }, []);

  // Для owner/admin — список працівників салону (селектор майстра)
  useEffect(() => {
    if (!isManager || !activeWorkspaceId) return;
    api.getWorkspaceMembers(activeWorkspaceId)
      .then((r) => setMembers(r.members || []))
      .catch(() => {});
  }, [isManager, activeWorkspaceId]);

  // ==== LOAD SERVICES (обраного майстра) ====
  const fetchServices = async () => {
    if (!effectiveMasterId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getServices(effectiveMasterId, activeWorkspaceId);
      setServices(data);
    } catch (e) {
      setError(e.message || t('service.loadServicesError'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line
  }, [effectiveMasterId, activeWorkspaceId]);

  // ==== UPDATE PROFILE ====
  const handleProfileSave = async () => {
    const errs = runValidators({
      name: validateName(profileForm.name),
      phone: validatePhone(profileForm.phone),
      email: validateEmail(profileForm.email), // опційний
    });
    setProfileErrors(errs);
    if (Object.keys(errs).length > 0) {
      setProfileError(t('common.checkFields'));
      return;
    }
    setProfileSaveLoading(true);
    setProfileError('');
    try {
      const updated = await api.updateUser(profile.id, {
        name: profileForm.name,
        phone: profileForm.phone,
        email: profileForm.email
      });
      setProfile(updated);
      setEditProfile(false);
    } catch (e) {
      setProfileError(e.message || t('service.saveProfileError'));
    }
    setProfileSaveLoading(false);
  };

  // Зібрати непорожні назви й провалідувати кожну; помилки — по ключах name_<lang>
  const buildNameErrors = (names) => {
    const errs = {};
    const filled = NAME_LANGS.filter((l) => names[l] && names[l].trim());
    if (filled.length === 0) {
      errs.names = 'service.atLeastOneName';
      return { errs, nameI18n: {} };
    }
    const nameI18n = {};
    for (const l of filled) {
      const e = validateServiceName(names[l]);
      if (e) errs[`name_${l}`] = e;
      else nameI18n[l] = names[l].trim();
    }
    return { errs, nameI18n };
  };

  // ==== AVATAR ====
  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // дозволити повторний вибір того самого файлу
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { setAvatarError(t('service.avatarType')); return; }
    if (file.size > 20 * 1024 * 1024) { setAvatarError(t('service.avatarSize')); return; }
    setAvatarError('');
    setAvatarUploading(true);
    try {
      const res = await api.uploadAvatar(file);
      setProfile((p) => ({ ...p, avatarUrl: res.avatarUrl }));
    } catch (err) {
      setAvatarError(err.message || t('service.avatarError'));
    }
    setAvatarUploading(false);
  };

  // ==== SERVICES LOGIC ====
  const addService = async () => {
    setAddLoading(true);
    setError('');
    try {
      if (!effectiveMasterId) throw new Error('User not found');
      const { errs: nameErrs, nameI18n } = buildNameErrors(newService.names);
      const errs = runValidators({
        ...nameErrs,
        price: validatePrice(newService.price),
        duration: validateDuration(newService.duration),
        // Порожнє поле = календарний час дорівнює часу роботи (бекенд підставить)
        blockDuration:
          newService.blockDuration === ''
            ? ''
            : validateDuration(newService.blockDuration) ||
              (Number(newService.blockDuration) < Number(newService.duration)
                ? 'validation.blockLessThanWork'
                : ''),
      });
      setServiceErrors(errs);
      if (Object.keys(errs).length > 0) {
        setError(t('common.checkFields'));
        setAddLoading(false);
        return;
      }
      const payload = {
        masterId: effectiveMasterId,
        nameI18n,
        price: parseFloat(newService.price),
        duration: parseInt(newService.duration),
        // порожнє → бекенд прирівняє до часу роботи
        blockDuration: newService.blockDuration === '' ? undefined : parseInt(newService.blockDuration),
      };
      await api.createService(payload);
      setNewService({ names: { pl: '', uk: '', en: '' }, price: '', duration: '', blockDuration: '' });
      fetchServices();
    } catch (e) {
      setError(e.message || t('service.addServiceError'));
    }
    setAddLoading(false);
  };

  // Відкрити редактор назв наявної послуги
  const startEditNames = (s) => {
    setEditingId(s.id);
    setEditNames({
      pl: s.nameI18n?.pl || '',
      uk: s.nameI18n?.uk || '',
      en: s.nameI18n?.en || '',
    });
    setEditTimes({
      price: String(s.price ?? ''),
      duration: String(s.duration ?? ''),
      blockDuration: String(s.blockDuration ?? s.duration ?? ''),
    });
    setServiceErrors({});
    setError('');
  };

  const saveEditNames = async () => {
    const { errs, nameI18n } = buildNameErrors(editNames);
    const timeErrs = runValidators({
      price: validatePrice(editTimes.price),
      duration: validateDuration(editTimes.duration),
      blockDuration:
        validateDuration(editTimes.blockDuration) ||
        (Number(editTimes.blockDuration) < Number(editTimes.duration)
          ? 'validation.blockLessThanWork'
          : ''),
    });
    const allErrs = { ...errs, ...timeErrs };
    setServiceErrors(allErrs);
    if (Object.keys(allErrs).length > 0) {
      setError(t('common.checkFields'));
      return;
    }
    setEditSaving(true);
    setError('');
    try {
      await api.updateService(editingId, {
        nameI18n,
        price: parseFloat(editTimes.price),
        duration: parseInt(editTimes.duration),
        blockDuration: parseInt(editTimes.blockDuration),
      });
      setEditingId(null);
      fetchServices();
    } catch (e) {
      setError(e.message || t('service.saveProfileError'));
    }
    setEditSaving(false);
  };

  const removeService = async (id) => {
    setLoading(true);
    setError('');
    try {
      await api.deleteService(id);
      fetchServices();
    } catch (e) {
      setError(e.message || t('service.deleteServiceError'));
      setLoading(false);
    }
  };

  // ==== STRIPE ACCOUNT LOGIC ====
  const handleCreateStripeAccount = async () => {
    try {
      const { url } = await api.createStripeAccount(profile.id);
      window.open(url, '_blank');
    } catch (e) {
      alert(e.message || t('service.stripeCreateError'));
    }
  };

  const handleRemoveStripeAccount = async () => {
    if (!window.confirm(t('service.stripeRemoveConfirm'))) return;
    try {
      await api.removeStripeAccount(profile.id);
      const updated = await api.getUsers().then(users => users.find(u => u.id === profile.id));
      setProfile(updated);
    } catch (e) {
      alert(e.message || t('service.stripeRemoveError'));
    }
  };

  const handleSetupStripeAccount = async () => {
    try {
      const { url } = await api.getStripeSetupLink(profile.id);
      window.open(url, '_blank');
    } catch (e) {
      alert(e.message || t('service.stripeSetupError'));
    }
  };

  return (
    <Container>
      <Typography variant="h5" gutterBottom>{t('service.profileTitle')}</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}
        {profileLoading ? (
          <CircularProgress />
        ) : (
          <Stack spacing={2}>
            {/* Аватар майстра */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={mediaUrl(profile?.avatarUrl)}
                sx={{ width: 72, height: 72, fontSize: 24 }}
              >
                {initialsOf(profile?.name)}
              </Avatar>
              <div>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  disabled={avatarUploading}
                >
                  {avatarUploading ? t('common.saving') : t('service.uploadAvatar')}
                  <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatar} />
                </Button>
                {avatarError && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {avatarError}
                  </Typography>
                )}
              </div>
            </Stack>

            {editProfile ? (
              <>
                <TextField
                  label={t('common.name')}
                  value={profileForm.name}
                  onChange={e => { setProfileForm({ ...profileForm, name: e.target.value }); setProfileErrors(p => ({ ...p, name: '' })); }}
                  error={!!profileErrors.name} helperText={profileErrors.name ? t(profileErrors.name) : ' '}
                  fullWidth
                />
                <TextField
                  label={t('common.phone')}
                  value={profileForm.phone}
                  placeholder="+48601234567"
                  onChange={e => { setProfileForm({ ...profileForm, phone: e.target.value }); setProfileErrors(p => ({ ...p, phone: '' })); }}
                  error={!!profileErrors.phone} helperText={t(profileErrors.phone || PHONE_RULE_KEY)}
                  fullWidth
                />
                <TextField
                  label={t('common.email')}
                  value={profileForm.email}
                  onChange={e => { setProfileForm({ ...profileForm, email: e.target.value }); setProfileErrors(p => ({ ...p, email: '' })); }}
                  error={!!profileErrors.email} helperText={profileErrors.email ? t(profileErrors.email) : ' '}
                  fullWidth
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleProfileSave}
                    disabled={profileSaveLoading}
                  >
                    {profileSaveLoading ? t('common.saving') : t('common.save')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditProfile(false);
                      setProfileForm({ name: profile.name, phone: profile.phone, email: profile.email });
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Typography>{t('common.name')}: <strong>{profile?.name}</strong></Typography>
                <Typography>{t('common.phone')}: <strong>{profile?.phone || '—'}</strong></Typography>
                <Typography>{t('common.email')}: <strong>{profile?.email || '—'}</strong></Typography>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditProfile(true)}
                >
                  {t('service.editProfile')}
                </Button>

                {/* Онлайн-оплати (Stripe) поки не пропонуємо — блок прихований.
                    Логіка (handleCreateStripeAccount тощо) лишається в коді для
                    майбутнього повернення фічі. */}
              </>
            )}
          </Stack>
        )}
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h5">
          {isManager && targetMasterId ? t('service.staffServices') : t('service.myServices')}
        </Typography>
        {isManager && members.length > 0 && (
          <TextField
            select
            size="small"
            label={t('service.forMaster')}
            value={targetMasterId || (myId ?? '')}
            onChange={(e) => setTargetMasterId(String(e.target.value) === String(myId) ? '' : e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {members.map((m) => (
              <MenuItem key={m.userId} value={m.userId}>
                {m.name}{String(m.userId) === String(myId) ? ` (${t('service.you')})` : ''}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading
        ? <CircularProgress sx={{ mt: 3 }} />
        : (
          <List>
            {services.map(s => (
              editingId === s.id ? (
                <ListItem key={s.id} sx={{ display: 'block' }}>
                  <Stack spacing={1.5} sx={{ width: '100%', py: 1 }}>
                    {NAME_LANGS.map((l) => (
                      <TextField
                        key={l}
                        size="small"
                        label={`${t('service.nameForLang')} · ${t(`lang.${l}`)}`}
                        value={editNames[l]}
                        onChange={(e) => { setEditNames(p => ({ ...p, [l]: e.target.value })); setServiceErrors(p => ({ ...p, [`name_${l}`]: '', names: '' })); }}
                        error={!!serviceErrors[`name_${l}`]}
                        helperText={serviceErrors[`name_${l}`] ? t(serviceErrors[`name_${l}`]) : ' '}
                        fullWidth
                      />
                    ))}
                    {serviceErrors.names && <Alert severity="error">{t(serviceErrors.names)}</Alert>}
                    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                      <TextField
                        size="small"
                        type="number"
                        label={t('service.price')}
                        value={editTimes.price}
                        onChange={(e) => { setEditTimes(p => ({ ...p, price: e.target.value })); setServiceErrors(p => ({ ...p, price: '' })); }}
                        error={!!serviceErrors.price}
                        helperText={serviceErrors.price ? t(serviceErrors.price) : ' '}
                        sx={{ flex: '1 1 140px' }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('service.durationLabel')}
                        value={editTimes.duration}
                        onChange={(e) => { setEditTimes(p => ({ ...p, duration: e.target.value })); setServiceErrors(p => ({ ...p, duration: '', blockDuration: '' })); }}
                        error={!!serviceErrors.duration}
                        helperText={serviceErrors.duration ? t(serviceErrors.duration) : ' '}
                        sx={{ flex: '1 1 200px' }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('service.blockDurationLabel')}
                        value={editTimes.blockDuration}
                        onChange={(e) => { setEditTimes(p => ({ ...p, blockDuration: e.target.value })); setServiceErrors(p => ({ ...p, blockDuration: '' })); }}
                        error={!!serviceErrors.blockDuration}
                        helperText={serviceErrors.blockDuration ? t(serviceErrors.blockDuration) : t('service.blockDurationHint')}
                        sx={{ flex: '1 1 200px' }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={saveEditNames} disabled={editSaving}>
                        {editSaving ? t('common.saving') : t('service.saveService')}
                      </Button>
                      <Button variant="outlined" onClick={() => setEditingId(null)} disabled={editSaving}>
                        {t('common.cancel')}
                      </Button>
                    </Stack>
                  </Stack>
                </ListItem>
              ) : (
                <ListItem key={s.id} secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <IconButton edge="end" aria-label={t('service.editName')} onClick={() => startEditNames(s)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => removeService(s.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                }>
                  <ListItemText
                    primary={`${pickServiceName(s, i18n.language)} – ${formatMoney(s.price)}`}
                    secondary={
                      `${s.duration} ${t('common.min')}` +
                      ((s.blockDuration ?? s.duration) !== s.duration
                        ? ` · ${t('service.blockDurationLabel')}: ${s.blockDuration} ${t('common.min')}`
                        : '')
                    }
                  />
                </ListItem>
              )
            ))}
            {!services.length && (
              <ListItem>
                <ListItemText primary={t('service.noServices')} />
              </ListItem>
            )}
          </List>
        )
      }

      <Stack spacing={2} sx={{ mt: 4 }}>
        <Typography variant="h6">{t('service.newService')}</Typography>
        {NAME_LANGS.map((l) => (
          <TextField
            key={l}
            label={`${t('service.nameForLang')} · ${t(`lang.${l}`)}`}
            value={newService.names[l]}
            onChange={(e) => { setNewService(p => ({ ...p, names: { ...p.names, [l]: e.target.value } })); setServiceErrors(p => ({ ...p, [`name_${l}`]: '', names: '' })); }}
            error={!!serviceErrors[`name_${l}`]}
            helperText={serviceErrors[`name_${l}`] ? t(serviceErrors[`name_${l}`]) : ' '}
          />
        ))}
        {serviceErrors.names && <Alert severity="error">{t(serviceErrors.names)}</Alert>}
        <TextField
          label={t('service.price')}
          type="number"
          value={newService.price}
          onChange={(e) => { setNewService({ ...newService, price: e.target.value }); setServiceErrors(p => ({ ...p, price: '' })); }}
          error={!!serviceErrors.price} helperText={serviceErrors.price ? t(serviceErrors.price) : ' '}
        />
        <TextField
          label={t('service.durationLabel')}
          type="number"
          value={newService.duration}
          onChange={(e) => { setNewService({ ...newService, duration: e.target.value }); setServiceErrors(p => ({ ...p, duration: '', blockDuration: '' })); }}
          error={!!serviceErrors.duration} helperText={serviceErrors.duration ? t(serviceErrors.duration) : ' '}
        />
        <TextField
          label={t('service.blockDurationLabel')}
          type="number"
          value={newService.blockDuration}
          onChange={(e) => { setNewService({ ...newService, blockDuration: e.target.value }); setServiceErrors(p => ({ ...p, blockDuration: '' })); }}
          error={!!serviceErrors.blockDuration}
          helperText={serviceErrors.blockDuration ? t(serviceErrors.blockDuration) : t('service.blockDurationHint')}
        />
        <Button
          variant="contained"
          onClick={addService}
          disabled={addLoading}
        >
          {addLoading ? t('service.adding') : t('common.add')}
        </Button>
      </Stack>
    </Container>
  );
}
